import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaEnvelope, FaExclamationTriangle, FaCheckCircle, FaArrowLeft } from 'react-icons/fa';

const Unsubscribe = () => {
  console.log('Unsubscribe component loaded');
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    reason: '',
    customReason: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    // URL'den email parametresini al
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setFormData(prev => ({ ...prev, email: emailParam }));
    }
  }, [searchParams]);

  const reasons = [
    { value: 'too-frequent', label: 'Emails are too frequent' },
    { value: 'not-relevant', label: 'Content is not relevant to me' },
    { value: 'never-signed-up', label: 'I never signed up for these emails' },
    { value: 'spam', label: 'I consider these emails as spam' },
    { value: 'other', label: 'Other reason' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch('/api/unsubscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setMessage(data.message);
        setMessageType('success');
        setIsSuccess(true);
      } else {
        setMessage(data.message || 'An error occurred');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleResubscribe = async () => {
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch('/api/unsubscribe/resubscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email })
      });

      const data = await response.json();

      if (data.success) {
        setMessage(data.message);
        setMessageType('success');
        setIsSuccess(false);
      } else {
        setMessage(data.message || 'An error occurred');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Simple Header */}
      <header style={{ backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '1rem 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827' }}>AppActs</h1>
            <button
              onClick={() => navigate('/')}
              style={{ color: '#2563eb', cursor: 'pointer', border: 'none', background: 'none' }}
            >
              Back to Home
            </button>
          </div>
        </div>
      </header>
      
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '3rem 1rem' }}>
        <div style={{ maxWidth: '42rem', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              width: '4rem', 
              height: '4rem', 
              backgroundColor: '#fef2f2', 
              borderRadius: '50%', 
              marginBottom: '1rem' 
            }}>
              <FaEnvelope style={{ width: '2rem', height: '2rem', color: '#dc2626' }} />
            </div>
            <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem' }}>
              Unsubscribe from AppActs
            </h1>
            <p style={{ color: '#6b7280' }}>
              We're sorry to see you go. Please let us know why you're unsubscribing.
            </p>
          </div>

          {/* Success Message */}
          {isSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
              <div className="flex items-center mb-4">
                <FaCheckCircle className="w-6 h-6 text-green-600 mr-3" />
                <h3 className="text-lg font-semibold text-green-800">
                  Successfully Unsubscribed
                </h3>
              </div>
              <p className="text-green-700 mb-4">
                You have been removed from our mailing list. You will no longer receive marketing emails from AppActs.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleResubscribe}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Processing...' : 'Resubscribe to Emails'}
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <FaArrowLeft className="inline mr-2" />
                  Back to Home
                </button>
              </div>
            </div>
          )}

          {/* Unsubscribe Form */}
          {!isSuccess && (
            <div className="bg-white rounded-lg shadow-lg p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>

                {/* Reason Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Why are you unsubscribing?
                  </label>
                  <div className="space-y-3">
                    {reasons.map((reason) => (
                      <label key={reason.value} className="flex items-center">
                        <input
                          type="radio"
                          name="reason"
                          value={reason.value}
                          checked={formData.reason === reason.value}
                          onChange={handleInputChange}
                          required
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="ml-3 text-sm text-gray-700">{reason.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Custom Reason */}
                {formData.reason === 'other' && (
                  <div>
                    <label htmlFor="customReason" className="block text-sm font-medium text-gray-700 mb-2">
                      Please specify your reason
                    </label>
                    <textarea
                      id="customReason"
                      name="customReason"
                      value={formData.customReason}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Please tell us more about why you're unsubscribing..."
                    />
                  </div>
                )}

                {/* Message Display */}
                {message && (
                  <div className={`p-4 rounded-lg ${
                    messageType === 'success' 
                      ? 'bg-green-50 border border-green-200 text-green-700' 
                      : 'bg-red-50 border border-red-200 text-red-700'
                  }`}>
                    <div className="flex items-center">
                      {messageType === 'success' ? (
                        <FaCheckCircle className="w-5 h-5 mr-2" />
                      ) : (
                        <FaExclamationTriangle className="w-5 h-5 mr-2" />
                      )}
                      {message}
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    type="submit"
                    disabled={loading || !formData.email || !formData.reason}
                    className="flex-1 bg-red-600 text-white py-3 px-6 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {loading ? 'Processing...' : 'Unsubscribe'}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="flex-1 bg-gray-600 text-white py-3 px-6 rounded-lg hover:bg-gray-700 transition-colors font-medium"
                  >
                    <FaArrowLeft className="inline mr-2" />
                    Back to Home
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Additional Information */}
          <div className="mt-8 text-center text-sm text-gray-600">
            <p>
              If you have any questions or concerns, please{' '}
              <a href="/contact" className="text-blue-600 hover:text-blue-700 underline">
                contact our support team
              </a>
              .
            </p>
          </div>
        </div>
      </div>

      {/* Simple Footer */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p>&copy; 2024 AppActs. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Unsubscribe;
